/*#
# Parent tasks

Work bigger than one task hangs its parts on a **parent** (board #755; a
generalization of an epic). It is one tag on the child, `parent:#12`, so
there is no new machinery: a task's children are the tasks that name it.

- **One parent, no cycles.** `parent` holds one value, like `project`.
  Parents nest: an epic of epics is a parent with a parent.
- **A parent is as complete as its least complete child, automatically**
  (owner, 2026-09-30). When a child moves, the store moves the parent in
  the same commit, attributed to whoever moved the child, and so on up the
  chain. Least complete is by lane order: inbox, freezer, backlog, ready,
  in-progress, review, done. A child that ended obsolete or rejected isn't
  holding anything back, so it doesn't count. When every counting child is
  done, the parent is done and closes; a new or reopened child reopens it.
- **A parent's status isn't set by hand** while it has children: the
  refusal says it follows its parts, and which are holding it back.
- **A parent doesn't drag its parts** (owner, 2026-10-06): it is
  organizational first. To move a whole piece of work, move its unfinished
  parts — `virta parts #12 freezer`, or the board's offer when you drag a
  parent — and the parent follows them.
- **Progress** is derived, never stored: `3/7` is three of seven counting
  children done.

```
virta child #12 "The first part"     # files a child of #12 (its project, kind, area, priority)
virta set #40 parent=#12             # makes an existing task a part of #12
virta parent #40 "The epic"          # files a parent for #40, at #40's status
virta parts #12 freezer              # moves #12's unfinished parts; #12 follows
virta list "parent:#12"              # its parts
```
*/

/*{ "parent": "Reference", "order": 42 }*/

import type { Task } from './model'
import { STATUS_ORDER, tagValues } from './tags'

export const PARENT_KEY = 'parent'

/** Endings that don't hold a parent back: the part isn't going to be done. */
const NOT_COUNTED = ['obsolete', 'rejected']

/** The parent a task names (`parent:#12` → 12), or null. */
export function parentNumOf(tags: Iterable<string>): number | null {
  for (const t of tags) {
    const m = /^parent:#(\d+)$/.exec(t)
    if (m) return Number(m[1])
  }
  return null
}

/** A task's status as a lane value: '' is the inbox. */
export const statusValue = (tags: Iterable<string>): string => {
  for (const t of tags) if (t.startsWith('status:')) return t.slice(7)
  return ''
}

/** Where a status sits in the lane order: lower is less complete. Unknown statuses sit with backlog. */
export function laneRank(status: string): number {
  const i = STATUS_ORDER.indexOf(status === '' ? 'inbox' : status)
  return i < 0 ? STATUS_ORDER.indexOf('backlog') : i
}

/** Whether a part counts toward its parent: anything not ended obsolete or rejected. */
export const counts = (status: string): boolean => !NOT_COUNTED.includes(status)

/**
 * What a parent's status should be, from its parts' statuses: the least
 * complete counting one. Null when no part counts (the parent is then its
 * own task again).
 */
export function leastComplete(statuses: readonly string[]): string | null {
  const counting = statuses.filter(counts)
  if (!counting.length) return null
  return counting.reduce((least, s) =>
    laneRank(s) < laneRank(least) ? s : least
  )
}

/** A parent's parts: the tasks tagged `parent:#<its number>`. */
export function childrenOf(tasks: Iterable<Task>, parent: Task): Task[] {
  const tag = `${PARENT_KEY}:#${parent.num}`
  const out: Task[] = []
  for (const t of tasks) if (t.tags.includes(tag)) out.push(t)
  return out.sort((a, b) => a.num - b.num)
}

export interface Progress {
  /** counting parts that are done */
  done: number
  /** counting parts */
  total: number
  /** every part, counted or not */
  parts: number
}

/** A parent's progress: `done` of `total` counting parts. */
export function progressOf(tasks: Iterable<Task>, parent: Task): Progress {
  const kids = childrenOf(tasks, parent)
  const counting = kids.filter((k) => counts(statusValue(k.tags)))
  return {
    done: counting.filter((k) => statusValue(k.tags) === 'done').length,
    total: counting.length,
    parts: kids.length,
  }
}

/** The inherited tags a new part starts with: where it belongs and what kind of work it is. */
export function partTags(parent: Task): string[] {
  return [
    ...parent.tags.filter((t) => /^(project|kind|area|priority):/.test(t)),
    `${PARENT_KEY}:#${parent.num}`,
  ]
}

/** Whether a task is a Library entry by tag: parents are for tasks. */
export const isLibraryEntry = (t: Pick<Task, 'tags'>) =>
  tagValues(t.tags, 'type').some((v) => v !== 'task')
